import Button from '@mui/material/Button';
import { FC, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { IScope } from '@bom-nextgen-keycloak/models';
import { selectPermissionScopeDetail } from '@bom-nextgen-keycloak/web/core';
import {
  ScopeDeleteDialog as DeleteDialog,
  useScope2,
} from '@bom-nextgen-keycloak/web/pages/feature-scope';
import { FeaturePage } from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  ProtectedRouter,
  TabbedContent,
  TabList,
} from '@bom-nextgen-keycloak/web/shared/ui';
import { useScopeDetail2 } from './hooks';
import { PARENT_PATH } from './shared';

const ScopeDetail2: FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const {
    canDelete,
    isDeleteDialogOpen,
    isDeleting,
    handleDeleteAction,
    handleDeleteCancel,
    handleDeleteRequest,
  } = useScope2({
    callback: () => navigate(PARENT_PATH),
    disableQuery: true,
  });
  const { data, isLoading } = useScopeDetail2(id);
  const { canViewGeneralTab } = useSelector(selectPermissionScopeDetail);

  const parentPath = PARENT_PATH;
  const rootPath = `${parentPath}/${id}`;
  const showDeleteButton = canDelete;
  const disableDeleteButton = isLoading;

  const tabList: TabList = [
    {
      enabled: canViewGeneralTab,
      label: 'General',
      value: '',
    },
  ];

  const itemRepresentation: IScope = {
    displayName: data?.displayName ?? '',
    id: data?.id ?? '',
    name: data?.name ?? '',
  };

  const handleDelete = () => {
    handleDeleteRequest(itemRepresentation);
  };

  const renderActions = () => {
    return showDeleteButton ? (
      <Button
        color="error"
        disabled={disableDeleteButton}
        onClick={handleDelete}
        variant="text"
      >
        Delete
      </Button>
    ) : null;
  };

  return (
    <Fragment>
      <TabbedContent
        actions={renderActions()}
        breadcrumb={data?.name}
        feature="Scopes"
        parentPath={parentPath}
        rootPath={rootPath}
        tabList={tabList}
      />
      <DeleteDialog
        loading={isDeleting}
        name={itemRepresentation.name}
        onClose={handleDeleteCancel}
        onSubmit={handleDeleteAction}
        open={isDeleteDialogOpen}
      />
    </Fragment>
  );
};

const WrappedScopeDetailPage: FC = () => {
  const { canViewPage } = useSelector(selectPermissionScopeDetail);

  return (
    <ProtectedRouter
      canView={canViewPage}
      feature={FeaturePage.SCOPE}
      isEditPage
      navigatePath={PARENT_PATH}
    >
      <ScopeDetail2 />
    </ProtectedRouter>
  );
};

export { WrappedScopeDetailPage };
